import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import StatusCard from './components/StatusCard';
import MetricCard from './components/MetricCard';
import ActivityFeed from './components/ActivityFeed';
import Icon from '../../components/AppIcon';
import LiveIndicator from '../../components/ui/LiveIndicator';
import NotificationCenter from '../../components/ui/NotificationCenter';
import { useRealtimeStatusCounts } from '../../hooks/useRealtimeStatusCounts';
import { dashboardService } from '../../services/dashboardService';
import RevenueChart from './components/RevenueChart';
import OutstandingInvoices from './components/OutstandingInvoices';
import PaymentMetrics from './components/PaymentMetrics';
import { useRealtimeFinancials } from '../../hooks/useRealtimeFinancials';
import AlertPanel from './components/AlertPanel';
import { useRealtimeAlerts } from '../../hooks/useRealtimeAlerts';
import { useRealtimeFeedbackAlerts } from '../../hooks/useRealtimeFeedbackAlerts';
import PartsCostBreakdown from './components/PartsCostBreakdown';
import LaborCostTracking from './components/LaborCostTracking';
import ProfitMarginAnalysis from './components/ProfitMarginAnalysis';
import { financialService } from '../../services/financialService';
import FeedbackAnalyticsSection from './components/FeedbackAnalyticsSection';
import { feedbackAnalyticsService } from '../../services/feedbackAnalyticsService';
import EvaModal from './components/EvaModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

// Normalize role names to lowercase internal values (mirrors MainSidebar logic)
const normalizeRole = (role) => {
  if (!role) return null;
  const map = {
    'TALLER': 'taller',
    'VENTAS': 'ventas',
    'ADMINISTRACION': 'admin',
    'DIRECCION': 'direccion',
    'RECEPCION': 'recepcion',
    'ADMIN': 'admin',
  };
  return map?.[role?.toUpperCase()] || role?.toLowerCase();
};

const WorkshopDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile, contextReady, loading } = useAuth();
  const userRole = normalizeRole(userProfile?.role) || (contextReady ? 'taller' : null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [partsCostData, setPartsCostData] = useState(null);
  const [laborCostData, setLaborCostData] = useState(null);
  const [profitMarginData, setProfitMarginData] = useState(null);
  const [costAnalyticsLoading, setCostAnalyticsLoading] = useState(true);
  const [highlightedFolio, setHighlightedFolio] = useState(null);
  const [feedbackAnalytics, setFeedbackAnalytics] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [evaModalOpen, setEvaModalOpen] = useState(false);
  const [solicitudesCount, setSolicitudesCount] = useState(0);
  const [solicitudesUrgent, setSolicitudesUrgent] = useState(false);
  const solicitudesIntervalRef = useRef(null);

  // Check for folio parameter from navigation
  useEffect(() => {
    const folio = searchParams?.get('folio');
    if (folio) {
      setHighlightedFolio(folio);
      // Show notification about authorized work order
      setTimeout(() => {
        alert(`Orden de trabajo ${folio} autorizada y lista para taller`);
      }, 500);
    }
  }, [searchParams]);

  const { 
    statusCounts, 
    loading: statusLoading, 
    error: statusError, 
    isLive: statusIsLive,
    lastUpdate: statusLastUpdate 
  } = useRealtimeStatusCounts();

  const {
    revenueTrends,
    outstandingInvoices,
    paymentRate,
    avgCollectionTime,
    loading: financialLoading,
    error: financialError,
    isLive: financialIsLive
  } = useRealtimeFinancials();

  const {
    alerts,
    loading: alertsLoading,
    error: alertsError,
    isLive: alertsIsLive
  } = useRealtimeAlerts();

  const {
    feedbackAlerts,
    loading: feedbackAlertsLoading,
    error: feedbackAlertsError,
    isLive: feedbackAlertsIsLive
  } = useRealtimeFeedbackAlerts();

  const workOrderStatuses = [
    { status: 'recepcionado', count: 12, label: 'Recepcionado' },
    { status: 'inspeccionado', count: 8, label: 'Inspeccionado' },
    { status: 'cotizando', count: 15, label: 'Cotizando' },
    { status: 'aprobado', count: 6, label: 'Aprobado' },
    { status: 'enproceso', count: 18, label: 'En Proceso' },
    { status: 'terminado', count: 9, label: 'Terminado' },
    { status: 'entregado', count: 24, label: 'Entregado' },
    { status: 'cancelado', count: 3, label: 'Cancelado' }
  ];

  const metrics = [
    {
      icon: 'DollarSign',
      label: 'Ingresos del Día',
      value: '$45,280',
      trend: 12.5,
      trendLabel: 'vs. ayer',
      color: 'success'
    },
    {
      icon: 'Clock',
      label: 'Inspecciones Pendientes',
      value: '8',
      trend: -5,
      trendLabel: 'vs. semana pasada',
      color: 'warning'
    },
    {
      icon: 'FileText',
      label: 'Cotizaciones Vencidas',
      value: '3',
      trend: null,
      trendLabel: 'Requieren atención',
      color: 'accent'
    },
    {
      icon: 'Wrench',
      label: 'Órdenes Activas',
      value: '18',
      trend: 8,
      trendLabel: 'vs. semana pasada',
      color: 'primary'
    }
  ];

  const handleStatusClick = (status) => {
    navigate('/work-order-timeline', { state: { filterStatus: status } });
  };

  const handleCreateWorkOrder = () => {
    navigate('/vehicle-reception');
  };

  const handleUpdateStatus = () => {
    navigate('/work-order-timeline');
  };

  const handleViewPending = () => {
    navigate('/work-order-timeline', { state: { filterStatus: 'pending' } });
  };

  const handleGenerateQuote = () => {
    navigate('/quotation-management');
  };

  const handleViewReports = () => {
    navigate('/workshop-dashboard');
  };

  const handleScheduleService = () => {
    navigate('/operational-calendar');
  };

  const fetchDashboardData = () => {
    setMetricsLoading(true);
    // Simulate data fetching - in real app this would be an API call
    setTimeout(() => {
      setMetricsLoading(false);
    }, 1000);
  };

  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      const activities = await dashboardService?.getRecentActivities(6);
      
      const formattedActivities = activities?.map(log => ({
        id: log?.id,
        type: log?.action === 'create' ? 'work-order' : 
              log?.action === 'status_change' ? 'update' :
              log?.action === 'approval' ? 'approval' : 'update',
        title: log?.action === 'create' ? 'Nueva Orden de Trabajo' :
               log?.action === 'status_change' ? 'Estado Actualizado' :
               log?.action === 'approval' ? 'Cotización Aprobada' : 'Actualización',
        description: `${log?.tableName} - ${log?.action}`,
        timestamp: new Date(log?.timestamp),
        user: 'Sistema'
      })) || [];

      setRecentActivities(formattedActivities);
    } catch (error) {
      console.error('Error fetching activities:', error);
      setRecentActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const fetchCostAnalytics = async () => {
    try {
      setCostAnalyticsLoading(true);
      const [partsCost, laborCost, profitMargin] = await Promise.all([
        financialService?.getPartsCostBreakdown(30),
        financialService?.getLaborCostTracking(30),
        financialService?.getProfitMarginAnalysis(30)
      ]);
      
      setPartsCostData(partsCost);
      setLaborCostData(laborCost);
      setProfitMarginData(profitMargin);
    } catch (error) {
      console.error('Error fetching cost analytics:', error);
    } finally {
      setCostAnalyticsLoading(false);
    }
  };

  const fetchFeedbackAnalytics = async () => {
    try {
      setFeedbackLoading(true);
      const analytics = await feedbackAnalyticsService?.getFeedbackAnalytics();
      setFeedbackAnalytics(analytics);
    } catch (error) {
      console.error('Error fetching feedback analytics:', error);
      setFeedbackAnalytics(null);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const fetchSolicitudesCount = async () => {
    try {
      const companyId = userProfile?.company_id;
      if (!companyId) return;
      const { data, error } = await supabase?.from('service_requests')?.select('id, urgency')?.eq('company_id', companyId)?.eq('status', 'nueva');
      if (error) return;
      const count = data?.length || 0;
      const hasUrgent = data?.some(r => r?.urgency === 'urgente') || false;
      setSolicitudesCount(count);
      setSolicitudesUrgent(hasUrgent);
    } catch (err) {
      console.error('Error fetching solicitudes count:', err);
    }
  };

  useEffect(() => {
    fetchActivities();
    fetchCostAnalytics();
    fetchFeedbackAnalytics();
    setMetricsLoading(false);
    return () => {
    };
  }, [userProfile?.company_id]);

  useEffect(() => {
    if (!['admin', 'recepcion', 'direccion', 'ventas']?.includes(userRole)) return;
    fetchSolicitudesCount();
    solicitudesIntervalRef.current = setInterval(fetchSolicitudesCount, 30000);
    return () => {
      if (solicitudesIntervalRef?.current) clearInterval(solicitudesIntervalRef?.current);
    };
  }, [userProfile?.company_id, userRole]);

  return (
    <div className="min-h-screen bg-background">
      <MainSidebar isCollapsed={sidebarCollapsed} />
      <main
        className={`
          transition-smooth
          ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}
        `}
      >
        <div className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2 pl-14 lg:pl-0">
                  <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground min-w-0 leading-tight">
                    {userRole === 'admin' || userRole === 'direccion' ?'Dashboard Administración'
                      : userRole === 'ventas' ?'Dashboard Ventas'
                      : userRole === 'recepcion' ?'Dashboard Recepción' :'Dashboard del Taller'}
                  </h1>
                  <LiveIndicator 
                    isLive={statusIsLive} 
                    lastUpdate={statusLastUpdate}
                  />
                  <NotificationCenter />
                </div>
                <p className="text-muted-foreground pl-14 lg:pl-0 mt-1">
                  Vista general de operaciones y métricas clave
                </p>
              </div>
              <QuickActionPanel
                userRole={userRole}
                onCreateWorkOrder={handleCreateWorkOrder}
                onUpdateStatus={handleUpdateStatus}
                onViewPending={handleViewPending}
                onGenerateQuote={handleGenerateQuote}
                onViewReports={handleViewReports}
                onScheduleService={handleScheduleService}
                onViewSolicitudes={() => navigate('/solicitudes-management')}
                solicitudesCount={solicitudesCount}
                solicitudesUrgent={solicitudesUrgent}
              />
              {/* EVA AI Button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setEvaModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20 transition-colors text-sm font-medium"
                >
                  <Icon name="Bot" size={16} />
                  <span>Preguntar a EVA</span>
                </button>
              </div>
            </div>

            {statusError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">{statusError}</p>
                </div>
              </div>
            )}

            {/* Metrics — render immediately, skeleton per card */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {metrics?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>

            {/* Financial Analytics Section */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  Análisis Financiero
                </h2>
                {financialIsLive && (
                  <div className="flex items-center gap-1.5 text-green-600 text-sm font-medium">
                    <Icon name="RefreshCw" className="w-4 h-4 animate-spin" />
                    <span>Actualizando</span>
                  </div>
                )}
              </div>

              {financialError && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{financialError}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2">
                  <RevenueChart data={revenueTrends} loading={financialLoading} />
                </div>
                <div className="lg:col-span-1">
                  <OutstandingInvoices invoices={outstandingInvoices} loading={financialLoading} />
                </div>
              </div>

              <PaymentMetrics 
                paymentRate={paymentRate} 
                avgCollectionTime={avgCollectionTime} 
                loading={financialLoading}
              />
            </div>

            {/* Cost Analytics Section */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-2xl font-heading font-bold text-foreground">
                  Análisis de Costos y Rentabilidad
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <PartsCostBreakdown data={partsCostData} loading={costAnalyticsLoading} />
                <LaborCostTracking data={laborCostData} loading={costAnalyticsLoading} />
              </div>

              <ProfitMarginAnalysis data={profitMarginData} loading={costAnalyticsLoading} />
            </div>

            {/* Customer Feedback Analytics Section */}
            <div className="mb-8">
              <FeedbackAnalyticsSection data={feedbackAnalytics} loading={feedbackLoading} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2">
                <div className="bg-card rounded-lg shadow-elevation-1 p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-heading font-semibold text-foreground">
                      Estado de Órdenes de Trabajo
                    </h2>
                    {statusIsLive && (
                      <div className="flex items-center gap-1.5 text-green-600 text-sm font-medium">
                        <Icon name="RefreshCw" className="w-4 h-4 animate-spin" />
                        <span>Actualizando</span>
                      </div>
                    )}
                  </div>
                  {statusLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[...Array(8)]?.map((_, i) => (
                        <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {statusCounts?.map((status) => (
                        <StatusCard
                          key={status?.status}
                          status={status?.status}
                          count={status?.count}
                          label={status?.label}
                          onClick={() => handleStatusClick(status?.status)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="bg-card rounded-lg shadow-elevation-1 p-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                    Actividad Reciente
                  </h2>
                  {activitiesLoading ? (
                    <div className="space-y-3">
                      {[...Array(4)]?.map((_, i) => (
                        <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <ActivityFeed activities={recentActivities} />
                  )}
                </div>
              </div>
            </div>

            {/* Threshold-Based Alerts Section */}
            <div className="mb-8">
              <AlertPanel alerts={alerts} loading={alertsLoading} />
              {alertsError && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">{alertsError}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <EvaModal
        isOpen={evaModalOpen}
        onClose={() => setEvaModalOpen(false)}
        contexto="Dashboard"
      />
    </div>
  );
};

export default WorkshopDashboard;